import {
  ChangeDetectionStrategy,
  Component,
  HostListener,
  computed,
  inject,
  input,
  signal,
} from '@angular/core';
import { CopilotPlaybooksService } from '../../services/copilot-playbooks.service';
import { CopilotFacadeService } from '../../services/copilot-facade.service';

/** Number of chips shown inline before the "More" overflow button appears. */
const VISIBLE_COUNT = 3;

@Component({
  selector: 'lib-copilot-playbooks-panel',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './copilot-playbooks-panel.component.html',
  styleUrl: './copilot-playbooks-panel.component.scss',
})
export class CopilotPlaybooksPanelComponent {
  private readonly playbooksService = inject(CopilotPlaybooksService);
  private readonly facade = inject(CopilotFacadeService);

  clientId = input<string | null>(null);
  conversationId = input<string | null>(null);
  loggedInUserId = input<string>('');
  isStreaming = input<boolean>(false);

  readonly playbooks = this.playbooksService.playbooks;
  readonly inFlight = computed(() => this.playbooksService.inFlight());
  readonly isEmpty = computed(() => this.playbooks.length === 0);

  // ── Overflow dropdown ───────────────────────────────────────────────────────
  readonly showOverflow = signal(false);

  readonly visiblePlaybooks = computed(() =>
    this.playbooks.slice(0, VISIBLE_COUNT)
  );

  readonly overflowPlaybooks = computed(() =>
    this.playbooks.slice(VISIBLE_COUNT)
  );

  readonly hasOverflow = computed(() => this.overflowPlaybooks().length > 0);

  readonly overflowLabel = computed(() => {
    const n = this.overflowPlaybooks().length;
    return `+${n} more`;
  });

  toggleOverflow(): void {
    this.showOverflow.update((v) => !v);
  }

  closeOverflow(): void {
    this.showOverflow.set(false);
  }

  /** Close overflow dropdown when clicking anywhere outside this component. */
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    // The template's (click).stopPropagation on the row prevents this from
    // firing for clicks inside the component.
    this.closeOverflow();
  }

  isDisabled(id: string): boolean {
    return this.isStreaming() || this.inFlight().has(id);
  }

  runPlaybook(id: string): void {
    const clientId = this.clientId();
    const conversationId = this.conversationId();
    if (!clientId || !conversationId) return;
    this.playbooksService.runPlaybook(
      id,
      this.facade,
      clientId,
      conversationId,
      this.loggedInUserId()
    );
  }

  runPlaybookAndClose(id: string): void {
    this.runPlaybook(id);
    this.closeOverflow();
  }
}
